// utils/errorHandler.ts
interface ValidationError {
  field: string;
  message: string;
}

interface ApiErrorResponse {
  success: boolean;
  message: string;
  errors?: ValidationError[];
}

interface ErrorWithResponse extends Error {
  response?: {
    data: ApiErrorResponse;
    status?: number;
  };
  code?: string;
}

/**
 * Extract a user-friendly error message from an API error
 *
 * @param err - Error object from API call
 * @returns User-friendly error message
 *
 * @example
 * ```typescript
 * try {
 *   await apiCall();
 * } catch (error) {
 *   const message = extractErrorMessage(error);
 *   toast.error(message);
 * }
 * ```
 */
export const extractErrorMessage = (err: unknown): string => {
  const error = err as ErrorWithResponse;

  // Check for structured API error response
  if (error.response?.data) {
    const data: ApiErrorResponse = error.response.data;

    // If there are validation errors, combine them
    if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      // Return all error messages joined together
      return data.errors.map((e) => e.message).join(". ");
    }

    // Otherwise return the main message
    if (data.message) {
      return data.message;
    }
  }

  // Network errors
  if (
    error.code === "ERR_NETWORK" ||
    error.message?.includes("Network Error")
  ) {
    return "Network error. Please check your internet connection and try again.";
  }

  // Timeout errors
  if (error.code === "ECONNABORTED" || error.message?.includes("timeout")) {
    return "Request timed out. Please try again.";
  }

  // Fallback to err.message or generic message
  return error.message || "An unexpected error occurred. Please try again.";
};

/**
 * Check if an error is an authentication error
 *
 * @param err - Error object from API call
 * @returns True if error is authentication-related
 */
export const isAuthenticationError = (err: unknown): boolean => {
  const error = err as ErrorWithResponse;

  // Check HTTP status codes
  if (error.response?.status === 401 || error.response?.status === 403) {
    return true;
  }

  // Check error message
  const message = extractErrorMessage(error).toLowerCase();
  return (
    message.includes("unauthorized") ||
    message.includes("unauthenticated") ||
    message.includes("forbidden") ||
    message.includes("not authenticated") ||
    message.includes("authentication required")
  );
};

/**
 * Check if an error is a validation error
 *
 * @param err - Error object from API call
 * @returns True if error is a validation error
 */
export const isValidationError = (err: unknown): boolean => {
  const error = err as ErrorWithResponse;

  if (error.response?.status === 400 || error.response?.status === 422) {
    return true;
  }

  const data = error.response?.data;
  return Boolean(
    data?.errors && Array.isArray(data.errors) && data.errors.length > 0
  );
};

/**
 * Get validation errors as a map of field -> message
 *
 * @param err - Error object from API call
 * @returns Map of field names to error messages
 */
export const getValidationErrors = (err: unknown): Record<string, string> => {
  const error = err as ErrorWithResponse;
  const errors: Record<string, string> = {};

  if (
    error.response?.data?.errors &&
    Array.isArray(error.response.data.errors)
  ) {
    error.response.data.errors.forEach((validationError) => {
      errors[validationError.field] = validationError.message;
    });
  }

  return errors;
};
